import React, { createContext, useContext, useState, useEffect } from 'react';
import { dashboardApi, notificationGenerators } from '../utils/dashboardApi';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Fetch notifications from dashboard data
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const data = await dashboardApi.fetchDashboardData();
      const generatedNotifications = [
        ...notificationGenerators.generateLowStockNotifications(data.inventory),
        ...notificationGenerators.generateLowPackingMaterialStockNotifications(data.pmInventory),
        ...notificationGenerators.generateOverdueOrderNotifications(data.salesOrders)
      ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      setNotifications(generatedNotifications);
      setUnreadCount(generatedNotifications.length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Mark notification as read
  const markAsRead = (notificationId) => {
    setNotifications(prev =>
      prev.map(notification =>
        notification.id === notificationId
          ? { ...notification, read: true }
          : notification
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  // Mark all notifications as read
  const markAllAsRead = () => {
    setNotifications(prev =>
      prev.map(notification => ({ ...notification, read: true }))
    );
    setUnreadCount(0);
  };

  // Add new notification
  const addNotification = (notification) => {
    const newNotification = {
      ...notification,
      id: notification.id || `notification-${Date.now()}`,
      timestamp: notification.timestamp || new Date(),
      read: false
    };
    setNotifications(prev => [newNotification, ...prev]);
    setUnreadCount(prev => prev + 1);
  };

  // Remove notification
  const removeNotification = (notificationId) => {
    setNotifications(prev => {
      const notification = prev.find(n => n.id === notificationId);
      const wasUnread = notification && !notification.read;
      const newNotifications = prev.filter(n => n.id !== notificationId);

      setNotifications(newNotifications);
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }

      return newNotifications;
    });
  };

  // Get notifications by type
  const getNotificationsByType = (type) => {
    return notifications.filter(notification => notification.type === type);
  };

  // Get unread notifications
  const getUnreadNotifications = () => {
    return notifications.filter(notification => !notification.read);
  };

  useEffect(() => {
    fetchNotifications();
    // Set up periodic refresh every 5 minutes
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const value = {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    addNotification,
    removeNotification,
    getNotificationsByType,
    getUnreadNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
